import React, { useState } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  Resolver,
  useForm,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  Grid,
  MenuItem,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { getNextConfigValues } from 'next-config';
import { ProgramAddress as Address } from 'src/ts/runtypes';
import { STATE_ABBREVIATIONS } from 'src/utils/constants';
import { InferType, object, string } from 'yup';

import TypeaheadTextField from './TypeaheadTextField';
import useNonNullContext from '../../../hooks/useNonNullContext';
import ConfigContext from '../../../context/config';
import DiscountForm from '../DiscountForm';

interface Props {
  handleSubmitAddress: (newAddress: Address) => Promise<void>;
  addressValidationError?: string;
  clearAddressValidationError: () => void;
  openCatalogPreview: () => void;
  verifyPostalCodeSupport: (postalCode: string) => Promise<boolean>;
  isPostalCodeSupported: boolean;
  isLoading: boolean;
  programId: number;
  setDiscountedGrandTotalInCents: (grandTotal: number) => void;
}

// form fields that can be submitted via auto-submit.
// intentionally a partial type so that when user clears street address,
// we can set that individual field to blank string while retaining others.
export type AutocompleteableAddressFields = Partial<
  Pick<Address, 'addressLine1' | 'state' | 'city' | 'zipCode' | 'giftNotes'>
>;

/**
 * custom styles to hide arrow buttons on numeric text input (for zip code)
 */
const numberInputStyles = {
  'input[type=number]': {
    MozAppearance: 'textfield',
  },
  'input[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  'input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

// map of under-the-hood field names to display names
const FIELD_TO_DISPLAY_NAME_MAP: Record<keyof Address, string> = {
  email: 'Email',
  recipient: 'Recipient name',
  addressLine1: 'Street address',
  addressLine2: 'Apt. or unit (optional)',
  city: 'City',
  state: 'State',
  zipCode: 'Zip code',
  deliveryNotes: 'Delivery notes (optional)',
  giftNotes: 'Personalized message (optional)',
  discountCode: 'Discount Code',
};

/**
 * Utility to get some helpful types around form error access. Two main purposes:
 *  - Forces us to access an actual form property rather than untyped dot notation
 *    to access error for a field.
 *  - We can replace the field name with the display name ("recipientName" => "Recipient name")
 */
const getErrorMessage = (
  errors: UseFormStateReturn<Address>['errors'],
  fieldName: keyof Address,
): string | undefined => {
  const err = errors[fieldName];

  const e = err?.message?.replace(
    fieldName,
    FIELD_TO_DISPLAY_NAME_MAP[fieldName],
  );
  return e;
};

/**
 * Helper component for rendering basic text inputs.
 *
 * Might extend this to handle some of the customization that other text inputs
 * are using; for now, just helps cut down on markdown clutter for those fields
 * where the form markup is the exact same save for the field name.
 */
const FormTextInput: React.FC<{
  field: keyof Address;
  control: Control<FieldValues, unknown>;
  defaultValue?: string;
  required?: boolean;
  rows?: number;
}> = ({ field, control, defaultValue, required, rows }) => {
  return (
    <Controller
      name={field}
      control={control}
      defaultValue={defaultValue ?? ''}
      render={({ field: { onChange, value }, formState: { errors } }) => {
        const errMsg = getErrorMessage(errors, field);
        return (
          <TextField
            color="primary"
            multiline={!!rows}
            rows={rows}
            fullWidth
            required={required}
            label={FIELD_TO_DISPLAY_NAME_MAP[field]}
            value={value}
            onChange={onChange}
            error={!!errMsg}
            helperText={errMsg}
          />
        );
      }}
    />
  );
};

const ZipCodeController: React.FC<{
  control: Control<{
    email: string;
    giftNotes?: string | undefined;
    recipient: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
  }>;
}> = ({ control }) => {
  return (
    <Controller
      name="zipCode"
      control={control}
      defaultValue=""
      render={({ field: { onChange, value }, formState: { errors } }) => {
        const errMsg = getErrorMessage(errors, 'zipCode');
        return (
          <TextField
            color="primary"
            required
            fullWidth
            // disable scroll from changing field value.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore TS wants us to use currentTarget but that doesn't work; this does.
            onWheel={(event) => event.target.blur()}
            label={FIELD_TO_DISPLAY_NAME_MAP.zipCode}
            value={value}
            onChange={(e) => {
              /**
               * Cap input length at 5 and prevent non-numeric inputs (Chrome for example
               * will allow 'e' to be entered for scientific notation).
               */
              const val = e.currentTarget.value;
              if (
                val !== '' && // only short circuit if changing to a non-blank value so user can still clear input.
                (val.length > 5 || isNaN(Number(val)))
              ) {
                return false;
              }
              onChange(e);
            }}
            error={!!errMsg}
            helperText={errMsg}
            sx={numberInputStyles}
          />
        );
      }}
    />
  );
};

const ProgramCheckoutForm: React.FC<Props> = ({
  handleSubmitAddress,
  addressValidationError,
  clearAddressValidationError,
  openCatalogPreview,
  verifyPostalCodeSupport,
  isPostalCodeSupported,
  isLoading,
  programId,
  setDiscountedGrandTotalInCents,
}) => {
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const addressSchema = object({
    email: string().required().email().defined(),
    recipient: string().required().defined().max(35),
    addressLine1: string().required().defined(),
    addressLine2: string().defined(),
    city: string().required().defined(),
    state: string().required().defined(),
    zipCode: string()
      .typeError('zip must be a valid number')
      .test('len', 'zip must be exactly 5 digits', (val) =>
        val ? val.toString().length === 5 : false,
      )
      .required(),
    giftNotes: string()
      .optional()
      .max(500)
      .test('line-breaks', 'Must have 10 or fewer lines', (val) => {
        if (!val) {
          return true;
        }
        const regex = /(\n|\r|\r\n)/g;
        const lineBreaks = val.match(regex)?.length || 0;
        return lineBreaks <= 10;
      }),
  });
  type AddressSchema = InferType<typeof addressSchema>;

  const { control, handleSubmit, setValue, formState } = useForm<AddressSchema>(
    {
      resolver: yupResolver(addressSchema) as Resolver<AddressSchema>,
      mode: 'all',
    },
  );

  const [appliedDiscountCode, setAppliedDiscountCode] = useState<
    string | undefined
  >();

  const onSubmit = (data: any, _e: any) => {
    handleSubmitAddress({
      ...data,
      discountCode: appliedDiscountCode,
    });
  };

  const onError = (_errors: any, _e: any) => {
    // @todo maybe need to render error message(s) but this might be ready to go
  };

  const watchedFields = useWatch({ control });

  // requirement is to clear address validation errors when any field changes; this is simplest way.
  React.useEffect(() => {
    // only call if the address validation error is present to as not to do unnecessary React state updates
    if (addressValidationError) {
      clearAddressValidationError();
    }
  }, [watchedFields]);

  const {
    publicRuntimeConfig: { SMARTY_STREETS_ENABLED },
  } = getNextConfigValues();

  /**
   * Helper to set form field values from autocomplete selection.
   */
  const setFormValuesFromAutocomplete = (
    fieldValues: AutocompleteableAddressFields,
  ) => {
    Object.entries(fieldValues).forEach(([fieldName, value]) => {
      setValue(fieldName as keyof AddressSchema, value, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    });
  };

  const [displayForm, setDisplayForm] = React.useState(false);

  const tenantConfig = useNonNullContext(ConfigContext);
  const enableGiftNotes = tenantConfig.enableGiftNotes;

  const [displayGiftNotesField, setDisplayGiftNotesField] =
    React.useState(false);

  const onViewMenuClick = async () => {
    const postalCode = watchedFields.zipCode;
    if (!postalCode) {
      return;
    }

    if (await verifyPostalCodeSupport(postalCode)) {
      openCatalogPreview();
    }
  };

  const onContinueToCheckoutClick = async () => {
    const postalCode = watchedFields.zipCode;
    if (!postalCode) {
      return;
    }

    if (await verifyPostalCodeSupport(postalCode)) {
      setDisplayForm(true);
    }
  };

  const zipCodeFieldState = control.getFieldState('zipCode', formState);
  const enableButtons =
    zipCodeFieldState.isDirty && !zipCodeFieldState.invalid && !isLoading;

  return (
    <>
      {!isPostalCodeSupported && (
        <Grid>
          <Typography variant={'caption'}>
            We’re sorry, delivery isn’t available to this area yet.
          </Typography>
        </Grid>
      )}
      {!displayForm && (
        <Grid
          container
          rowSpacing={2}
          sx={{
            width: isXs ? '100%' : '25rem',
            paddingLeft: isXs ? 0 : '1rem',
            paddingRight: isXs ? 0 : '1rem',
            paddingTop: '1rem',
            paddingBottom: '1rem',
            margin: 'auto',
            justifyContent: 'center',
          }}
        >
          <Grid>
            <Typography variant="subtitle2">
              Please provide your zip code to continue:
            </Typography>
          </Grid>
          <Grid size={12}>
            <ZipCodeController control={control} />
          </Grid>
          <Grid container columnSpacing={2}>
            <Grid>
              <Button
                variant={'outlined'}
                disabled={!enableButtons}
                onClick={onViewMenuClick}
              >
                View Menu
              </Button>
            </Grid>
            <Grid
              sx={{
                flexGrow: 1,
              }}
            >
              <Button
                color={'primary'}
                fullWidth
                variant={'contained'}
                disabled={!enableButtons}
                onClick={onContinueToCheckoutClick}
              >
                Continue to Checkout
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
      {displayForm && (
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid
            container
            sx={{
              flexDirection: 'column',
              width: isXs ? '100%' : '25rem',
              paddingLeft: isXs ? 0 : '1rem',
              paddingRight: isXs ? 0 : '1rem',
              paddingTop: '1rem',
              paddingBottom: '1rem',
              margin: 'auto',
            }}
          >
            <Grid size={12}>
              <Grid container spacing={2} sx={{ flexDirection: 'column' }}>
                <Grid size={12}>
                  <FormTextInput
                    field={'email'}
                    control={
                      control as unknown as Control<FieldValues, unknown>
                    }
                    required
                  />
                </Grid>
                <Grid size={12}>
                  <FormTextInput
                    field={'recipient'}
                    control={
                      control as unknown as Control<FieldValues, unknown>
                    }
                    required
                  />
                </Grid>
                {/* street address */}
                <Grid size={12}>
                  {SMARTY_STREETS_ENABLED ? (
                    <Controller
                      name="addressLine1"
                      control={control}
                      render={({
                        field: { onChange },
                        formState: { errors },
                      }) => (
                        <TypeaheadTextField
                          label={FIELD_TO_DISPLAY_NAME_MAP.addressLine1}
                          onSelect={setFormValuesFromAutocomplete}
                          onChange={onChange}
                          errorMessage={getErrorMessage(errors, 'addressLine1')}
                        />
                      )}
                    />
                  ) : (
                    <FormTextInput
                      field="addressLine1"
                      control={
                        control as unknown as Control<FieldValues, unknown>
                      }
                      required
                    />
                  )}
                </Grid>
                {/* unit */}
                <Grid size={12}>
                  <FormTextInput
                    field="addressLine2"
                    control={
                      control as unknown as Control<FieldValues, unknown>
                    }
                  />
                </Grid>
                {/* city */}
                <Grid size={12}>
                  <FormTextInput
                    field="city"
                    control={
                      control as unknown as Control<FieldValues, unknown>
                    }
                    required
                  />
                </Grid>
                {/* state and zip  */}
                <Grid size={12}>
                  <Grid container spacing={2}>
                    <Grid size={8}>
                      <Controller
                        name="state"
                        control={control}
                        defaultValue=""
                        render={({
                          field: { onChange, value },
                          formState: { errors },
                        }) => {
                          const errMsg = getErrorMessage(errors, 'state');
                          return (
                            <TextField
                              select
                              color="primary"
                              required
                              fullWidth
                              label={FIELD_TO_DISPLAY_NAME_MAP.state}
                              value={value}
                              onChange={onChange}
                              error={!!errMsg}
                              helperText={errMsg}
                              sx={{
                                textAlign: 'left',
                              }}
                            >
                              {STATE_ABBREVIATIONS.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </TextField>
                          );
                        }}
                      />
                    </Grid>
                    <Grid size={4}>
                      <ZipCodeController control={control} />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <FormTextInput
                    field={'deliveryNotes'}
                    control={
                      control as unknown as Control<FieldValues, unknown>
                    }
                  />
                </Grid>

                {/* Gift Notes */}
                {!!enableGiftNotes && (
                  <Grid size={12}>
                    {displayGiftNotesField ? (
                      <FormTextInput
                        field={'giftNotes'}
                        rows={3}
                        control={
                          control as unknown as Control<FieldValues, unknown>
                        }
                      />
                    ) : (
                      <Typography
                        variant={'subtitle2'}
                        onClick={() => setDisplayGiftNotesField(true)}
                        sx={{ textAlign: 'left', cursor: 'pointer' }}
                      >
                        Add a personalized note
                      </Typography>
                    )}
                  </Grid>
                )}

                {addressValidationError && (
                  <Grid>
                    <Alert severity="error">{addressValidationError}</Alert>
                  </Grid>
                )}
                <Grid container>
                  <DiscountForm
                    programId={programId}
                    setAppliedDiscountCode={setAppliedDiscountCode}
                    setDiscountedGrandTotalInCents={
                      setDiscountedGrandTotalInCents
                    }
                    email={
                      control.getFieldState('email', formState).invalid
                        ? undefined
                        : watchedFields.email
                    }
                  />
                </Grid>
                {/* submit  */}
                <Grid container columnSpacing={2}>
                  <Grid>
                    <Button
                      variant={'outlined'}
                      fullWidth
                      disabled={!enableButtons}
                      onClick={onViewMenuClick}
                    >
                      View Menu
                    </Button>
                  </Grid>
                  <Grid
                    sx={{
                      flexGrow: 1,
                    }}
                  >
                    <Button
                      color="primary"
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={
                        !formState.isValid ||
                        !!addressValidationError ||
                        isLoading
                      }
                    >
                      Continue to checkout
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
};

export default ProgramCheckoutForm;
